import { AnswerTabsShimmer } from "./AnswerSheetShimmer";
import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useMutation } from "@tanstack/react-query";
import ReportModal from "../../components/ReportModal";
import StickyNotesPopup from "../../components/StickyNotesPopup";
import ViewModal from "../../components/ViewModal";
import {
  toggleQuestionBookmark,
  toggleQuestionLike,
} from "../../services/questionBankQuestionService";
import { saveQuizGuessAnswer } from "../../services/quizQuestionService";
import "./AnswerTab.css";

const tabConfig = [
  { id: "all", icon: "bi-list-check", label: "All" },
  { id: "correct", icon: "bi-check-circle", label: "Correct" },
  { id: "incorrect", icon: "bi-x-circle", label: "Incorrect" },
  { id: "unsolved", icon: "bi-arrow-repeat", label: "Unsolved" },
];

const AnswerTabs = ({
  questions = [],
  isLoading = false,
  guessPayloadBase = {},
  enableGuessToggle = false,
  activeTab: controlledActiveTab,
  onTabChange,
  hideTabs = false,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState("all");

  const activeTab = controlledActiveTab ?? internalActiveTab;

  const handleTabChange = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };
  const counts = useMemo(
    () => ({
      all: questions.length,
      correct: questions.filter((question) => question.status === "correct").length,
      incorrect: questions.filter((question) => question.status === "incorrect").length,
      unsolved: questions.filter((question) => question.status === "unsolved").length,
    }),
    [questions]
  );

  const filteredQuestions = useMemo(() => {
    if (activeTab === "all") {
      return questions;
    }

    return questions.filter((question) => question.status === activeTab);
  }, [activeTab, questions]);

  if (isLoading) {
    return <AnswerTabsShimmer hideTabs={hideTabs} />;
  }

  return (
    <div className="quiz-result-page">
      <div className="quiz-result-container">
      {!hideTabs && (
  <div className="quiz-question-tabs">
    {tabConfig.map((tab) => {
      const isActive = activeTab === tab.id;

      return (
        <button
          key={tab.id}
          type="button"
          className={`quiz-question-tab quiz-tab-${tab.id} ${
            isActive ? "active" : ""
          }`}
          onClick={() => handleTabChange(tab.id)}
        >
          <i className={`bi ${tab.icon} quiz-tab-icon`} />
          <span className="quiz-tab-count">
            {counts[tab.id]}
          </span>
        </button>
      );
    })}
  </div>
)}

        <div className="">
          <div className="">
            <div className="quiz-question-list">
              {filteredQuestions.length ? (
                filteredQuestions.map((question) => (
                  <QuestionCard
                    key={`${question.id}-${question.questionIndex}`}
                    question={question}
                    guessPayloadBase={guessPayloadBase}
                    enableGuessToggle={enableGuessToggle}
                  />
                ))
              ) : (
                <div className="quiz-empty">
                  <i className="bi bi-inbox" />
                  <h3>No Questions Found</h3>
                  <p>There are no questions in this category.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionCard = ({ question, guessPayloadBase = {}, enableGuessToggle = false }) => {
  const [isExplanationOpen, setIsExplanationOpen] = useState(true);
  const [openReport, setOpenReport] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [questionState, setQuestionState] = useState(question);
  const bookmarkMutation = useMutation({
    mutationFn: toggleQuestionBookmark,
  });
  const likeMutation = useMutation({
    mutationFn: toggleQuestionLike,
  });
  const guessMutation = useMutation({
    mutationFn: saveQuizGuessAnswer,
  });

  useEffect(() => {
    setQuestionState(question);
  }, [question]);

  const getStatusIcon = () => {
    if (questionState.status === "correct") {
      return "bi-check-circle-fill";
    }
    if (questionState.status === "incorrect") {
      return "bi-x-circle-fill";
    }
    return "bi-exclamation-circle-fill";
  };

  const getStatusText = () => {
    if (questionState.status === "correct") {
      return "CORRECT";
    }
    if (questionState.status === "incorrect") {
      return "INCORRECT";
    }
    return "UNSOLVED";
  };

  const handleToggleBookmark = async () => {
    if (!questionState?.id || bookmarkMutation.isPending) {
      return;
    }

    const previousValue = Boolean(questionState.isBookmarked);
    setQuestionState((current) => ({
      ...current,
      isBookmarked: !previousValue,
    }));

    try {
      const result = await bookmarkMutation.mutateAsync({
        question_id: Number(questionState.id),
      });

      setQuestionState((current) => ({
        ...current,
        isBookmarked: Number(result?.payload?.bookmark_status ?? 0) === 1,
      }));
    } catch (_error) {
      setQuestionState((current) => ({
        ...current,
        isBookmarked: previousValue,
      }));
    }
  };

  const handleToggleLike = async () => {
    if (!questionState?.id || likeMutation.isPending) {
      return;
    }

    const previousLiked = Boolean(questionState.isLiked);
    const previousCount = Number(questionState.likeCount || 0);
    setQuestionState((current) => ({
      ...current,
      isLiked: !previousLiked,
      likeCount: Math.max(0, previousCount + (previousLiked ? -1 : 1)),
    }));

    try {
      const result = await likeMutation.mutateAsync({
        question_id: Number(questionState.id),
      });

      setQuestionState((current) => ({
        ...current,
        isLiked: Number(result?.payload?.like_status ?? 0) === 1,
        likeCount: Number(result?.payload?.total_liked ?? previousCount),
      }));
    } catch (_error) {
      setQuestionState((current) => ({
        ...current,
        isLiked: previousLiked,
        likeCount: previousCount,
      }));
    }
  };

  const handleToggleGuess = async (nextValue) => {
    if (
      !enableGuessToggle ||
      !questionState?.id ||
      !["correct", "incorrect"].includes(questionState.status) ||
      guessMutation.isPending
    ) {
      return;
    }

    const previousValue = Boolean(questionState.isGuess);
    setQuestionState((current) => ({
      ...current,
      isGuess: nextValue,
    }));

    try {
      await guessMutation.mutateAsync({
        ...guessPayloadBase,
        question_id: Number(questionState.id),
        is_guess: nextValue ? 1 : 0,
      });
    } catch (_error) {
      setQuestionState((current) => ({
        ...current,
        isGuess: previousValue,
      }));
    }
  };

  const isGuessDisabled =
    !enableGuessToggle ||
    !["correct", "incorrect"].includes(questionState.status) ||
    guessMutation.isPending;

  return (
    <>
      <div className="quiz-question-card">
        <div className="quiz-card-header">
          <div className={`quiz-status status-${questionState.status}`}>
            <i className={`bi ${getStatusIcon()}`} aria-hidden="true" />
            <span>{getStatusText()}</span>
          </div>

          <div className="quiz-card-time">
            <i className="bi bi-clock" aria-hidden="true" />
            <span>{questionState.time}</span>
          </div>

          <button
            type="button"
            className={`quiz-bookmark-button${questionState.isBookmarked ? " is-active" : ""}`}
            aria-label={`Bookmark question ${questionState.id}`}
            onClick={handleToggleBookmark}
          >
            <i
              className={`bi ${questionState.isBookmarked ? "bi-bookmark-fill" : "bi-bookmark"}`}
              aria-hidden="true"
            />
          </button>
        </div>

        <div className="quiz-question-text">
          <span className="quiz-question-number">Q {questionState.questionIndex}. </span>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                questionState.questionHtml || questionState.question || ""
              ),
            }}
          />
        </div>

        <div className="qbank-options" role="list" aria-label="Answer options">
          {questionState.options.map((option) => (
            <button
              type="button"
              key={`${questionState.id}-${option.id}`}
              className={`qbank-option qbank-option--${option.type}`}
              aria-label={`Option ${option.key}`}
            >
              <span className="qbank-option__badge">{option.key}</span>

              <span className="qbank-option__copy answerOption">
                <span
                  className="qbank-option__english"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(option.english || ""),
                  }}
                />

                {option.hindi ? (
                  <span
                    className="qbank-option__hindi"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(option.hindi),
                    }}
                  />
                ) : null}
              </span>
            </button>
          ))}
        </div>

        <label className="markGuess qbank-guess" htmlFor={`guess-answer-${questionState.id}`}>
          <span className="qbank-guess__check">
            <input
              id={`guess-answer-${questionState.id}`}
              type="checkbox"
              checked={questionState.isGuess}
              onChange={(event) => handleToggleGuess(event.target.checked)}
              disabled={isGuessDisabled}
            />
            <span className="qbank-guess__box" aria-hidden="true" />
          </span>

          <span className="qbank-guess__label">Mark as guess</span>
        </label>

        <section className="qbank-sideCard">
          <div className="qbank-sideCard__head">
            <h3>Explanation</h3>

            <button
              type="button"
              className="qbank-sideCard__toggle"
              aria-label={isExplanationOpen ? "Collapse explanation" : "Expand explanation"}
              onClick={() => setIsExplanationOpen((previous) => !previous)}
            >
              <i className={`bi ${isExplanationOpen ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" />
            </button>
          </div>

          {isExplanationOpen ? (
            <div className="qbank-sideCard__body">
              <div className="qbank-sideCard__subtitle">
                {questionState.explanation ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(questionState.explanation),
                    }}
                  />
                ) : (
                  <span>No explanation available.</span>
                )}
              </div>
            </div>
          ) : null}
        </section>

        <div className="qbl-questionCard__footer">
          <button
            type="button"
            className="qbl-questionCard__footerItem"
            onClick={handleToggleLike}
          >
            <i
              className={`bi ${questionState.isLiked ? "bi-heart-fill" : "bi-heart"}`}
              aria-hidden="true"
            />
            <span>Like</span>
            <strong>{questionState.likeCount}</strong>
          </button>

          <button
            type="button"
            className="qbl-questionCard__footerItem"
            onClick={() => setOpenView(true)}
          >
            <i className="bi bi-eye" aria-hidden="true" />
            <span>View</span>
            <strong>{questionState.viewCount}</strong>
          </button>

          <button
            type="button"
            className="qbl-questionCard__footerItem"
            onClick={() => setOpenReport(true)}
          >
            <i className="bi bi-exclamation-circle" aria-hidden="true" />
            <span>Report</span>
            <strong>{questionState.reportCount}</strong>
          </button>

          <button
            type="button"
            className="qbl-questionCard__footerItem"
            onClick={() => setOpenNotes(true)}
          >
            <i className="bi bi-journal-text" aria-hidden="true" />
            <span>Notes</span>
            <strong>{questionState.notesCount}</strong>
          </button>
        </div>
      </div>

      <ViewModal open={openView} onClose={() => setOpenView(false)} question={questionState} />
      <ReportModal open={openReport} onClose={() => setOpenReport(false)} question={questionState} />
      <StickyNotesPopup open={openNotes} onClose={() => setOpenNotes(false)} question={questionState} />
    </>
  );
};

export default AnswerTabs;
